/**
 * Portfolios — the lower half of the dashboard rail: scope switch, search, and
 * the portfolio list.
 *
 * Port of the legacy Vue app's SideBarDashboard/Portfolios.vue.
 *
 * The scope is a string, not a boolean — see portfolios.js. The switch label
 * is the scope itself, as in legacy, so it always states what you are looking
 * at rather than what toggling would do.
 *
 * THE LIST is GET /get-portfolio (api/portfoliosApi.js) for the signed-in user.
 * One response carries both scopes, so the switch and the search box only
 * re-slice what is already loaded — neither makes a request. The fetch is
 * skipped until there is a signed-in user to ask for, rather than guessing an
 * address.
 *
 * Each row: model mark (Scaler / SPEC CPU), provider mark (or a key for a
 * cloud-credential portfolio), the name, and — for a large-data portfolio still
 * processing — a refresh icon (GET /job-status, see RefreshButton) and its
 * progress %. Not yet here: the telemetry PENDING / in-progress marks (fed by
 * the notifications poll).
 *
 * SELECTING a row does not load anything here. It checks what legacy checks
 * first (a busy large-data portfolio — portfolios.js)
 * and then REQUESTS the open through the slice. Manage Portfolio answers it,
 * because only the page knows about unsaved work and so whether to ask
 * "discard changes?" first. The same goes for the ✕ (start a new portfolio).
 * The open row is highlighted from the slice's `openPortfolio`.
 *
 * The scope switch asks "Please Confirm" first, and Proceed goes to the page as
 * a { kind: 'scope' } request (unsaved check, then a fresh page in the new
 * scope). It is for ADMINS only, as legacy (Portfolios.vue :5-16, the
 * `CCA_role` / `EIA_role` === 'admin' check): anyone else sees it disabled,
 * with "Only admins can view all portfolios" on hover. The server's `admin`
 * role is the app's VIEW_ORG_PORTFOLIOS permission (features/auth/roles.js),
 * checked with can() — never a role string here.
 */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@components/common/ConfirmDialog';
import { EllipsisText } from '@components/common/EllipsisText';
import { FloatingTooltip } from '@components/common/FloatingTooltip';
import { TextField } from '@components/common/TextField';
import {
  ClearIcon,
  KeyIcon,
  ClockIcon,
  RefreshAlertIcon,
  RefreshIcon,
  SearchIcon,
} from '@components/common/icons';
import { Alert, Button, Spinner, Switch, Tooltip } from '@components/ui';
import { useNotify } from '@hooks/useNotify';
import { cx } from '@lib/classNames';
import { selectRoles, selectUser } from '@security/auth/authSlice';
import { PERMISSIONS, can } from '@security/permissions';

import {
  useGetPortfoliosQuery,
  useRefreshPortfolioProgressMutation,
} from '../../api/portfoliosApi';
import { BENCHMARK_MODEL_ICONS, BENCHMARK_MODELS } from '../../benchmarkModels';
import {
  CLEAR_PORTFOLIO_LABEL,
  NO_PORTFOLIOS_MESSAGE,
  PORTFOLIO_SCOPES,
  PORTFOLIO_SEARCH_PLACEHOLDER,
  SCOPE_ADMIN_ONLY_HINT,
  filterPortfolios,
  isScalerPortfolio,
  largeDataProgress,
  largeDataRefresh,
  portfolioMark,
  portfolioName,
  portfolioOpenTarget,
  portfolioOpenWarning,
  portfoliosForScope,
  telemetryJobWarning,
  toOpenPortfolio,
  toggleScope,
} from '../../portfolios';
import { PROVIDER_ICONS, TELEMETRY_CONNECTORS, isBillingMode } from '../../serviceProviders';
import {
  requestPortfolio,
  selectOpenPortfolio,
  selectPortfolioScope,
  selectPortfolioSearch,
  selectSelectedTelemetry,
  selectTelemetryJobStatus,
  setPortfolioScope,
  setPortfolioSearch,
} from '../../slice';

import styles from './Portfolios.module.css';

/**
 * The two leading marks on a row: model, then provider (or credential key).
 *
 * No hover tooltips here: in this list a tooltip appears ONLY on a name that is
 * cut off. The marks keep their text alternatives for screen readers.
 */
function RowMarks({ portfolio }) {
  const model = isScalerPortfolio(portfolio)
    ? BENCHMARK_MODELS.GENERAL_IT_AVERAGE
    : BENCHMARK_MODELS.SPEC_CPU;
  const mark = portfolioMark(portfolio);

  return (
    <>
      <img className={styles.mark} src={BENCHMARK_MODEL_ICONS[model]} alt={model} />
      {mark === 'cloud-credential' ? (
        <span className={styles.keyMark}>
          <KeyIcon />
          <span className="sr-only">Cloud credentials</span>
        </span>
      ) : mark && PROVIDER_ICONS[mark] ? (
        <img className={styles.mark} src={PROVIDER_ICONS[mark]} alt={mark} />
      ) : (
        // Keeps names aligned in a column when a provider has no logo.
        <span className={styles.markSpacer} aria-hidden="true" />
      )}
    </>
  );
}

/**
 * The refresh icon on a big portfolio still being worked on: asks the server
 * how far along it is (GET /job-status) and updates the row's %; at 100 the
 * list is fetched again and the portfolio becomes openable.
 *
 * Each row owns its request, so one row refreshing never blocks the others —
 * legacy instead covers the WHOLE page with its loader for every click.
 *
 * Failure stays on the row (there is no toast system yet): the icon turns to
 * the error colour and its label says to try again. Legacy shows a global alert.
 */
function RefreshButton({ portfolio, name, userEmail }) {
  const kind = largeDataRefresh(portfolio);
  const [refresh, { isLoading, isError }] = useRefreshPortfolioProgressMutation();

  if (!kind) return null;

  const label = isError
    ? `Could not refresh ${name}. Try again.`
    : kind === 'not-started'
      ? `Not started yet. Refresh progress of ${name}`
      : `Refresh progress of ${name}`;
  const Icon = kind === 'not-started' ? RefreshAlertIcon : RefreshIcon;

  return (
    // Floating: this sits inside the scrolling list, which would clip an
    // in-place tooltip bubble at its edges.
    <FloatingTooltip label={isError ? 'Could not refresh — try again' : 'Refresh'} side="bottom">
      <button
        type="button"
        className={cx(
          styles.refresh,
          isLoading && styles.refreshing,
          isError && styles.refreshError,
        )}
        aria-label={label}
        aria-busy={isLoading || undefined}
        disabled={isLoading}
        // The row's open button is a sibling, not a parent, so a refresh can
        // never also open the portfolio; stopPropagation is belt-and-braces.
        onClick={(e) => {
          e.stopPropagation();
          refresh({ portfolioId: portfolio._id, userEmail });
        }}
      >
        <Icon />
      </button>
    </FloatingTooltip>
  );
}

/**
 * A telemetry portfolio the server is still reading (legacy Portfolios.vue
 * :72-82): a clock while it waits, a spinner while it works. Floating hint,
 * like the refresh icon's — the list scrolls and would clip one in place.
 */
function TelemetryMark({ status, name }) {
  if (status !== 'pending' && status !== 'in_progress') return null;
  const pending = status === 'pending';
  const label = pending ? 'Pending' : 'In Progress';
  return (
    <FloatingTooltip label={label} side="bottom">
      <span
        className={pending ? styles.jobPending : styles.jobRunning}
        role="img"
        aria-label={`${name}: ${label}`}
      >
        {pending ? <ClockIcon /> : <Spinner small />}
      </span>
    </FloatingTooltip>
  );
}

function PortfolioList({
  portfolios,
  userEmail,
  openId,
  onOpen,
  jobs,
  isLoading,
  isError,
  onRetry,
}) {
  // A portfolio opened from a notification (or any way other than clicking its
  // row) can sit far down the list, out of sight. Bring the highlighted row into
  // view when it becomes the open one, or when the list it is in appears (a
  // scope switch). 'nearest' leaves a row that is already visible where it is.
  // Keyed on whether the open row is in the list, not on the list itself (a new
  // array every render): re-running on every render would pull the list back
  // while the user scrolls it by hand.
  const activeRef = useRef(null);
  const hasActive = Boolean(openId) && portfolios.some((p) => p._id === openId);
  useEffect(() => {
    if (hasActive) activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [openId, hasActive, isLoading]);

  if (isLoading) {
    return (
      <div className={styles.state}>
        <Spinner small />
      </div>
    );
  }

  // A failed load is said out loud, with a retry. Legacy swallows the error
  // and silently shows an empty list, indistinguishable from "no portfolios".
  if (isError) {
    return (
      <Alert variant="danger" className={styles.alert}>
        Could not load portfolios.{' '}
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (portfolios.length === 0) {
    return <p className={styles.state}>{NO_PORTFOLIOS_MESSAGE}</p>;
  }

  return (
    <ul className={styles.list} aria-label="Portfolio list">
      {portfolios.map((portfolio, index) => {
        const name = portfolioName(portfolio);
        const progress = largeDataProgress(portfolio);
        const active = Boolean(openId) && portfolio._id === openId;
        return (
          // Keyed by _id, NOT by position. A row's refresh state (busy, error)
          // lives in the row; a position-based key changes whenever search or
          // scope reorders the list, which remounted the row mid-request — the
          // spinner vanished, the button re-enabled and an error label was
          // cleared without any retry. Name + index only for a row with no _id.
          <li
            key={portfolio._id ?? `${name}_${index}`}
            ref={active ? activeRef : undefined}
            className={cx(styles.item, active && styles.active)}
          >
            {/* The open button covers marks + name only; refresh stays its own
                button beside it (a button inside a button is invalid HTML). */}
            <button
              type="button"
              className={styles.open}
              aria-current={active ? 'true' : undefined}
              onClick={() => onOpen(portfolio)}
            >
              <RowMarks portfolio={portfolio} />
              {/* Cut off with "…" when too long; the full name shows in a
                  tooltip only then — never on a name that already fits. */}
              <span className={styles.name} data-portfolio-name>
                <EllipsisText>{name}</EllipsisText>
              </span>
            </button>
            {/* Legacy's order: refresh icon, then the %. */}
            <TelemetryMark status={jobs?.[portfolio._id]} name={name} />
            <RefreshButton portfolio={portfolio} name={name} userEmail={userEmail} />
            {progress === null ? null : <span className={styles.progress}>{progress} %</span>}
          </li>
        );
      })}
    </ul>
  );
}

export function Portfolios() {
  const dispatch = useDispatch();
  const scope = useSelector(selectPortfolioScope);
  const search = useSelector(selectPortfolioSearch);
  const userEmail = useSelector(selectUser)?.email ?? '';
  const canViewOrg = can(useSelector(selectRoles), PERMISSIONS.VIEW_ORG_PORTFOLIOS);
  const telemetry = useSelector(selectSelectedTelemetry);
  const openId = useSelector(selectOpenPortfolio)?.id ?? null;
  const jobs = useSelector(selectTelemetryJobStatus);
  const notify = useNotify();

  const { data, isLoading, isFetching, isError, refetch } = useGetPortfoliosQuery(userEmail, {
    skip: !userEmail,
  });

  const portfolios = filterPortfolios(portfoliosForScope(data, scope), search);

  /**
   * Open a row. `connector` is the telemetry connector the portfolio belongs
   * to — the sidebar's pick for a click on a row; the portfolio's own provider
   * when a notification opens it (legacy sets the service from it first).
   */
  const onOpen = (portfolio, connector = telemetry) => {
    const warning = portfolioOpenWarning(portfolio);
    if (warning) {
      notify.warning(warning);
      return;
    }
    // A telemetry job still running (clock / spinner on the row): its list would
    // be empty, so it opens only once the job is done. Nothing on the page changes.
    const jobWarning = telemetryJobWarning(portfolio, jobs?.[portfolio?._id]);
    if (jobWarning) {
      notify.warning(jobWarning);
      return;
    }
    const target = portfolioOpenTarget(portfolio, { telemetry: connector });
    // `target` says WHERE it opens: 'manage' (Manage Portfolio), 'telemetry'
    // (the Telemetry page's read-only view, for `connector`), 'cloud-credential'
    // or 'cost-advice' (a finished large-data portfolio goes to its advice).
    // With Billing picked it opens on Manage Portfolio in Billing (CUR) mode.
    dispatch(
      requestPortfolio({
        kind: 'open',
        portfolio: toOpenPortfolio(portfolio, { billing: isBillingMode(connector) }),
        target,
        ...(target === 'telemetry' ? { connector } : {}),
      }),
    );
  };

  /*
   * A notification click lands here with { openPortfolioId } in the navigation
   * state (features/notifications NotificationsMenu). Find that portfolio in
   * the lists this user may see — My, and Org for an admin only — (legacy
   * looked only in the scope on screen and silently did nothing otherwise),
   * switch to its scope so the row shows, and open it like
   * a click on the row. Not in the cached list (made after it was fetched)?
   * Fetch once more before saying so. Handled once per navigation, then the
   * state is cleared so a reload or Back does not open it again.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const pendingId = location.state?.openPortfolioId ?? null;
  const refetchedFor = useRef(null);

  useEffect(() => {
    if (!pendingId || !data || isFetching) return;
    const inOwn = data.own.find((p) => p?._id === pendingId);
    // The Org list only for an admin: anyone else can never be put in Org
    // scope (legacy: the switch is admin-only, Portfolios.vue :13).
    const match = inOwn ?? (canViewOrg ? data.team.find((p) => p?._id === pendingId) : undefined);
    if (!match && refetchedFor.current !== location.key) {
      refetchedFor.current = location.key;
      refetch();
      return;
    }
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    if (!match) {
      notify.info('That portfolio is no longer in your portfolio list.');
      return;
    }
    const matchScope = inOwn ? PORTFOLIO_SCOPES.OWN : PORTFOLIO_SCOPES.TEAM;
    // Both clear the search box, so the highlighted row is not filtered out.
    dispatch(matchScope === scope ? setPortfolioSearch('') : setPortfolioScope(matchScope));
    const provider = String(match.provider ?? '').toLowerCase();
    const connector = TELEMETRY_CONNECTORS.find((c) => c.toLowerCase() === provider) ?? '';
    onOpen(match, connector);
    // onOpen / scope are read at the moment the state arrives; re-running on
    // their changes would open the portfolio twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingId, data, isFetching, location.key]);

  /*
   * My / Org switch: "Please Confirm" first (legacy Portfolios.vue :114-131).
   * Proceed goes through the page like a portfolio click, so unsaved work is
   * asked about there, and the page starts afresh in the new scope (legacy
   * clearPortfoliosData). The switch itself never flips on its own — it shows
   * the slice's scope, which only changes once that has happened.
   */
  const [scopeDialog, setScopeDialog] = useState(false);
  const onProceedScope = () => {
    setScopeDialog(false);
    dispatch(requestPortfolio({ kind: 'scope', scope: toggleScope(scope) }));
  };

  // Legacy addPortfolio: blank the search, then start a new portfolio.
  const onNew = () => {
    dispatch(setPortfolioSearch(''));
    dispatch(requestPortfolio({ kind: 'new' }));
  };

  return (
    <section className={styles.portfolios} aria-label="Portfolios">
      <div className={styles.header}>
        {/* Legacy's tooltip: the admin-only hint for everyone else, the
            scope itself for an admin (Portfolios.vue :6). */}
        <Tooltip label={canViewOrg ? scope : SCOPE_ADMIN_ONLY_HINT} side="bottom">
          <span className={styles.scope}>
            <Switch
              id="portfolio-scope"
              label={scope}
              checked={scope === PORTFOLIO_SCOPES.TEAM}
              disabled={!canViewOrg}
              onChange={() => setScopeDialog(true)}
            />
          </span>
        </Tooltip>

        {/* Closes the open portfolio and starts a blank one (legacy addPortfolio). */}
        <Tooltip label={CLEAR_PORTFOLIO_LABEL} side="bottom">
          <button
            type="button"
            className={styles.clear}
            aria-label={CLEAR_PORTFOLIO_LABEL}
            onClick={onNew}
          >
            <ClearIcon />
          </button>
        </Tooltip>
      </div>

      <TextField
        id="portfolio-search"
        placeholder={PORTFOLIO_SEARCH_PLACEHOLDER}
        value={search}
        onChange={(e) => dispatch(setPortfolioSearch(e.target.value))}
        leadingIcon={<SearchIcon />}
        clearable
        hideDetails
      />

      <PortfolioList
        portfolios={portfolios}
        userEmail={userEmail}
        openId={openId}
        onOpen={onOpen}
        jobs={jobs}
        // A retry after an error is a fetch with no data yet — show the spinner.
        isLoading={isLoading || (isFetching && isError)}
        isError={isError && !isFetching}
        onRetry={refetch}
      />

      {scopeDialog ? (
        // Legacy's wording and buttons (Portfolios.vue :114-131).
        <ConfirmDialog
          title="Please Confirm"
          message={`You are about to switch to viewing ${toggleScope(scope)}. Do you want to proceed?`}
          cancelLabel="Cancel"
          confirmLabel="Proceed"
          onConfirm={onProceedScope}
          onCancel={() => setScopeDialog(false)}
        />
      ) : null}
    </section>
  );
}

export default Portfolios;
